import { useEffect, useRef, useState } from "react";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import {
  AdoRunStatus,
  IAdoRefreshStatus,
  getAdoRefreshStatus,
  triggerAdoRefresh,
} from "../api/aiMetricsApi";
import { ADO_QUERY_KEYS, ADO_REFRESH_INVALIDATE_KEYS } from "./adoQueryKeys";

const REFRESH_STATUS_STALE_TIME_MS = 30_000;
const IN_PROGRESS_POLL_INTERVAL_MS = 2000;

export type RefreshPhase =
  "idle" | "in_progress" | "success" | "failure" | "discarded";

const FAILURE_MESSAGE =
  "The refresh did not complete. Please try again or contact your administrator.";
const DISCARDED_MESSAGE =
  "Refresh could not be started — no processing capacity was available. Please try again.";
const DEFAULT_TRIGGER_ERROR = "Unable to start the refresh. Please try again.";

const NON_SUCCESS_TERMINAL_ADO_RUN_STATUSES: readonly (AdoRunStatus | null)[] =
  ["failure", "discarded"];
const fetchAdoRefreshStatusAndInvalidateOnCompletion = async (
  queryClient: QueryClient,
): Promise<IAdoRefreshStatus> => {
  const previousStatus = queryClient.getQueryData<IAdoRefreshStatus>([
    ADO_QUERY_KEYS.refreshStatus,
  ]);

  const nextStatus = await getAdoRefreshStatus();
  const previousSuccessRunId = previousStatus?.last_refresh?.id ?? null;
  const nextSuccessRunId = nextStatus.last_refresh?.id ?? null;
  const newSuccessObserved =
    previousStatus !== undefined &&
    nextSuccessRunId !== null &&
    nextSuccessRunId !== previousSuccessRunId;

  const nonSuccessTerminalObserved =
    previousStatus?.in_progress === true &&
    nextStatus.in_progress === false &&
    NON_SUCCESS_TERMINAL_ADO_RUN_STATUSES.includes(nextStatus.active_status);

  const justCompleted = newSuccessObserved || nonSuccessTerminalObserved;

  if (justCompleted) {
    ADO_REFRESH_INVALIDATE_KEYS.filter(
      (key) => key !== ADO_QUERY_KEYS.refreshStatus,
    ).forEach((key) => {
      void queryClient.invalidateQueries([key]);
    });
  }

  return nextStatus;
};

export const useAdoRefreshStatus = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [ADO_QUERY_KEYS.refreshStatus],
    queryFn: () => fetchAdoRefreshStatusAndInvalidateOnCompletion(queryClient),
    staleTime: REFRESH_STATUS_STALE_TIME_MS,
    refetchInterval: (data) =>
      data?.in_progress ? IN_PROGRESS_POLL_INTERVAL_MS : false,
  });
};

export interface ITriggerRefreshOptions {
  onConflict?: () => void;
}

export interface IUseAdoRefreshResult {
  triggerRefresh: (options?: ITriggerRefreshOptions) => void;
  isRefreshing: boolean;
  refreshPhase: RefreshPhase;
  activeStatus: AdoRunStatus | null;
  lastError: string | null;
}

export const useAdoRefresh = (): IUseAdoRefreshResult => {
  const queryClient = useQueryClient();
  const [refreshPhase, setRefreshPhase] = useState<RefreshPhase>("idle");
  const [lastError, setLastError] = useState<string | null>(null);

  const refreshStatus = useAdoRefreshStatus();
  const inProgress = refreshStatus.data?.in_progress ?? false;
  const activeStatus = refreshStatus.data?.active_status ?? null;

  const wasInProgress = useRef(false);
  useEffect(() => {
    if (inProgress) {
      wasInProgress.current = true;
      if (refreshPhase !== "in_progress") setRefreshPhase("in_progress");
      return;
    }
    if (!wasInProgress.current) return;
    wasInProgress.current = false;

    if (activeStatus === "success") {
      setLastError(null);
      setRefreshPhase("success");
    } else if (activeStatus === "discarded") {
      setLastError(DISCARDED_MESSAGE);
      setRefreshPhase("discarded");
    } else if (activeStatus === "failure") {
      setLastError(refreshStatus.data?.last_refresh?.error ?? FAILURE_MESSAGE);
      setRefreshPhase("failure");
    } else {
      setRefreshPhase("idle");
    }
  }, [inProgress, activeStatus]);

  const onConflictRef = useRef<(() => void) | undefined>(undefined);

  const mutation = useMutation(triggerAdoRefresh, {
    onMutate: () => {
      setLastError(null);
      setRefreshPhase("in_progress");
    },
    onSuccess: () => {
      wasInProgress.current = true;
      void queryClient.invalidateQueries([ADO_QUERY_KEYS.refreshStatus]);
    },
    onError: (error: Error & { status?: number }) => {
      if (error.status === 409) {
        void queryClient.invalidateQueries([ADO_QUERY_KEYS.refreshStatus]);
        setRefreshPhase("in_progress");
        onConflictRef.current?.();
      } else {
        setLastError(error.message ?? DEFAULT_TRIGGER_ERROR);
        setRefreshPhase("failure");
      }
    },
  });

  const isRefreshing = mutation.isLoading || inProgress;

  return {
    triggerRefresh: (options?: ITriggerRefreshOptions) => {
      onConflictRef.current = options?.onConflict;
      mutation.mutate();
    },
    isRefreshing,
    refreshPhase,
    activeStatus,
    lastError,
  };
};
